// Copyright 2019 Sorama B.V.

import { Point2D } from "datatypes/SoramaLocalApi";
import { Size } from "libraries/DataTypes/Size";

export class Quad {
    public topLeft: Point2D;
    public topRight: Point2D;
    public bottomLeft: Point2D;
    public bottomRight: Point2D;

    constructor(topLeft?: Point2D, topRight?: Point2D, bottomLeft?: Point2D, bottomRight?: Point2D) {
        this.topLeft = topLeft ? topLeft : { x: 0, y: 0 };
        this.topRight = topRight ? topRight : { x: 1, y: 0 };
        this.bottomLeft = bottomLeft ? bottomLeft : { x: 0, y: 1 };
        this.bottomRight = bottomRight ? bottomRight : { x: 1, y: 1 };
    }

    //#region Helper functions

    public static equals(a: Quad, b: Quad): boolean {
        return (
            Math.abs(a.topLeft.x - b.topLeft.x) < 1e-5 &&
            Math.abs(a.topLeft.y - b.topLeft.y) < 1e-5 &&
            Math.abs(a.topRight.x - b.topRight.x) < 1e-5 &&
            Math.abs(a.topRight.y - b.topRight.y) < 1e-5 &&
            Math.abs(a.bottomLeft.x - b.bottomLeft.x) < 1e-5 &&
            Math.abs(a.bottomLeft.y - b.bottomLeft.y) < 1e-5 &&
            Math.abs(a.bottomRight.x - b.bottomRight.x) < 1e-5 &&
            Math.abs(a.bottomRight.y - b.bottomRight.y) < 1e-5
        );
    }

    public static toArray(quad: Quad): number[] {
        return [
            quad.topLeft.x,
            quad.topLeft.y,
            quad.topRight.x,
            quad.topRight.y,
            quad.bottomLeft.x,
            quad.bottomLeft.y,
            quad.bottomRight.x,
            quad.bottomRight.y,
        ];
    }

    public static scale(quad: Quad, x: number, y: number, anchorx = 0, anchory = 0): Quad {
        return {
            topLeft: { x: (quad.topLeft.x - anchorx) * x + anchorx, y: (quad.topLeft.y - anchory) * y + anchory },
            topRight: { x: (quad.topRight.x - anchorx) * x + anchorx, y: (quad.topRight.y - anchory) * y + anchory },
            bottomLeft: {
                x: (quad.bottomLeft.x - anchorx) * x + anchorx,
                y: (quad.bottomLeft.y - anchory) * y + anchory,
            },
            bottomRight: {
                x: (quad.bottomRight.x - anchorx) * x + anchorx,
                y: (quad.bottomRight.y - anchory) * y + anchory,
            },
        };
    }

    public static translate(quad: Quad, x: number, y: number): Quad {
        return {
            topLeft: { x: quad.topLeft.x + x, y: quad.topLeft.y + y },
            topRight: { x: quad.topRight.x + x, y: quad.topRight.y + y },
            bottomLeft: { x: quad.bottomLeft.x + x, y: quad.bottomLeft.y + y },
            bottomRight: { x: quad.bottomRight.x + x, y: quad.bottomRight.y + y },
        };
    }

    public static clone(quad: Quad): Quad {
        return new Quad(
            { x: quad.topLeft.x, y: quad.topLeft.y },
            { x: quad.topRight.x, y: quad.topRight.y },
            { x: quad.bottomLeft.x, y: quad.bottomLeft.y },
            { x: quad.bottomRight.x, y: quad.bottomRight.y }
        );
    }

    //#endregion

    //#region Factory Methods/Getters

    public static get unitQuad(): Quad {
        return {
            topLeft: { x: 0, y: 0 },
            topRight: { x: 1, y: 0 },
            bottomLeft: { x: 0, y: 1 },
            bottomRight: { x: 1, y: 1 },
        };
    }

    /**
     * Create a new quad with a given aspectratio that contains the provided area.
     * @param aspectRatio Aspect ratio of the returned quad
     * @param area
     */
    public static fitQuadOverArea(aspectRatio: number, area: Size): Quad {
        // Set region to fit within the image
        const scale = Math.min(aspectRatio / area.width, 1.0 / area.height);
        const width = aspectRatio / (scale * area.width);
        const height = 1.0 / (scale * area.height);

        const left = ((1 - width) / 2) * area.width;
        const right = ((1 - width) / 2 + width) * area.width;
        const top = ((1 - height) / 2) * area.height;
        const bottom = ((1 - height) / 2 + height) * area.height;

        return new Quad({ x: left, y: top }, { x: right, y: top }, { x: left, y: bottom }, { x: right, y: bottom });
    }

    /**
     * Create a new quad with a given aspectratio that fits in the provided area.
     * @param aspectRatio Aspect ratio of the returned quad
     * @param area
     */
    public static fitQuadInArea(aspectRatio: number, area: Size): Quad {
        // Set region to fit within the image
        const scale = Math.max(aspectRatio / area.width, 1.0 / area.height);
        const width = aspectRatio / (scale * area.width);
        const height = 1.0 / (scale * area.height);

        const left = ((1 - width) / 2) * area.width;
        const right = ((1 - width) / 2 + width) * area.width;
        const top = ((1 - height) / 2) * area.height;
        const bottom = ((1 - height) / 2 + height) * area.height;

        return new Quad({ x: left, y: top }, { x: right, y: top }, { x: left, y: bottom }, { x: right, y: bottom });
    }

    public static quadBoundingBox(quad: Quad): Quad {
        const xmin = Math.min(quad.bottomLeft.x, quad.bottomRight.x, quad.topLeft.x, quad.topRight.x);
        const xmax = Math.max(quad.bottomLeft.x, quad.bottomRight.x, quad.topLeft.x, quad.topRight.x);
        const ymin = Math.min(quad.bottomLeft.y, quad.bottomRight.y, quad.topLeft.y, quad.topRight.y);
        const ymax = Math.max(quad.bottomLeft.y, quad.bottomRight.y, quad.topLeft.y, quad.topRight.y);
        return {
            bottomLeft: {
                x: xmin,
                y: ymax,
            },
            bottomRight: {
                x: xmax,
                y: ymax,
            },
            topLeft: {
                x: xmin,
                y: ymin,
            },
            topRight: {
                x: xmax,
                y: ymin,
            },
        };
    }

    //#endregion
}
